// TIP-493 (MV Stage 4b) — "Buy a Web Profile" payment form (left column of step 2).
//
// Drives create_payment for the chosen option:
//   mode 'payg'   → one-time PAYG invoice (standard rate).
//   mode 'member' → one-time PAYG invoice; backend applies the member coupon
//                   (resolved from acting_company_id).
//   mode 'bundle' → membership Subscription whose first invoice also carries the
//                   discounted PAYG line (recurring=true, bundle=true, plan).
// Then auto-finalizes to reveal the Stripe PaymentElement and pay in one step.
import React, { useEffect, useState, useCallback, useRef } from "react";
import plusCircle from "../../../../assets/icons/plusCircle.png";
import { ReactComponent as LocationPin } from "../../../../assets/icons/find.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "../../../../utils/stripe";
import CheckoutForm from "./checkoutForm";
import { useSelector } from "react-redux";
import {
  useCreateStripePaymentMutation,
  useFinalizeStripePaymentMutation,
} from "../../../../store/Company/companyApiSlice";
import { selectActiveCompanyId } from "../../../../store/activeCompanySlice";
import { Modal, LoadingIcon, Button } from "../../../common";
import { BillingAddress } from "../../../common/BillingAddress";
import { AddBillingAddress } from "../../../common/AddBillingAddress";
import { EditBillingAddress } from "../../../common/EditBillingAddress";
import {
  useGetBillingAddressByBillingIdQuery,
  useGetBillingAddressByUserIdQuery,
  useDeleteBillingAddressMutation,
} from "../../../../store/Billing/billingApiSlice";
import { CommonToast } from "../../../common/Toast";
import LottieAnimation from "../../SuccessAnimation";
import { PLAN_YEARLY } from "../../../../constants/webProfilePricing";
import { Aed } from "../../../common/Money";

export const PaymentDetailsForm = ({ planDetails, summary, copy, setPaymentBreakdown, paymentBreakdown, isReportRequest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo, token } = useSelector((state) => state.auth);
  const actingCompanyId = useSelector(selectActiveCompanyId);
  const stateData = location.state;

  const mode = planDetails?.mode || "payg";
  const membershipPlan = planDetails?.plan || PLAN_YEARLY;
  const isBundle = mode === "bundle";
  const recurring = isBundle;

  const [userBillingAddress, setUserBillingAddress] = useState(false);
  const [paymentSuccessfulModal, setPaymentSuccessfulModal] = useState(false);
  const [pendingSetup, setPendingSetup] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [showStripePayment, setShowStripePayment] = useState(false);
  const [openBillingPopup, setOpenBillingPopup] = useState(false);
  const [openAddBillingAddressPopup, setOpenAddBillingAddressPopup] = useState(false);
  const [openEditBillingAddressPopup, setOpenEditBillingAddressPopup] = useState(false);
  const [billingId, setBillingId] = useState("");
  const [userID, setUserID] = useState(null);
  const [hasAddressDataChanged, setHasAddressDataChanged] = useState(false);

  const [deleteBillingAddresss] = useDeleteBillingAddressMutation();
  const [createStripePayment, { isLoading: isCreateLoading }] = useCreateStripePaymentMutation();
  const [finalizeStripePayment] = useFinalizeStripePaymentMutation("");

  // TIP-614 — the shared instance. This was called in the component body, so
  // a fresh Stripe.js promise was created on every render.
  const options = { clientSecret, appearance: { theme: "stripe" } };

  const { data, refetch } = useGetBillingAddressByUserIdQuery({ id: userID, token });
  const checkPrimaryAvailable = data ? data.filter((i) => i?.is_primary == true) : [];
  const shippingAddress = checkPrimaryAvailable.length > 0 ? checkPrimaryAvailable : data;

  const primary = shippingAddress?.[0];

  useEffect(() => {
    const id = userInfo?.individual?.[0]?.individualID;
    setUserID(id);
  }, [userInfo]);

  // TIP-493 — stash the flow so the (post-Stripe-redirect) success page can pick
  // the right copy; localStorage survives the redirect, React state doesn't.
  useEffect(() => {
    localStorage.setItem("webProfilePurchaseMode", mode);
  }, [mode]);

  useEffect(() => {
    setUserBillingAddress(Boolean(data && data.length > 0));
    refetch();
  }, [data, refetch, hasAddressDataChanged]);

  // TIP-646 — the invoice a re-quote supersedes, so the backend can void it
  // instead of leaking one per address change. Held in a ref, NOT read from
  // `paymentBreakdown`: a re-quote clears that state before the debounced call
  // runs, which would lose the id and leak the invoice.
  const lastInvoiceIdRef = useRef(paymentBreakdown?.invoice_id || null);

  // TIP-646 — quotes are raised per billing address, and the address list can
  // flap while a primary-flag PATCH settles. Two guards keep the summary
  // truthful: this counter makes the LAST-ISSUED request the winner (a slower
  // earlier response is discarded rather than overwriting a newer quote), and
  // the effect below debounces so the flapping settles before we quote at all.
  // Without them a stale UAE quote could land after the Norway one and show
  // 5% VAT on an order Stripe will charge 0% on.
  const requestSeqRef = useRef(0);

  const createPaymentEvent = useCallback(async () => {
    const meta_data = {
      createWorldbox: localStorage.getItem("createWorldbox"),
      worldboxdata: localStorage.getItem("worldboxData"),
      companyDataString: JSON.parse(localStorage.getItem("payasyougoCompanyID")),
      paycompanyStatus: localStorage.getItem("paycompanyStatus"),
      billing_add_ID: shippingAddress?.[0]?.billing_add_ID,
      UserID: userInfo?.individual?.[0]?.userID,
      // TIP-564 — these two only belong to the report-order flow; sending them
      // unconditionally let a stale flag from an abandoned report order ride
      // along on a later, unrelated purchase's meta_data.
      ...(isReportRequest && {
        createWorldBoxCompanyData: localStorage.getItem("createWorldboxCompany"),
        companyDescription: localStorage.getItem("companyDescription"),
      }),
    };
    const mySeq = ++requestSeqRef.current;
    const supersededInvoiceId = lastInvoiceIdRef.current;
    try {
      const response = await createStripePayment({
        data: {
          recurring,
          bundle: isBundle,
          plan: isBundle ? membershipPlan : undefined,
          product_type: "PAY-AS-YOU-GO",
          acting_company_id: actingCompanyId,
          promocode: "",
          purchase_track_id: stateData?.purchase_track_id || "",
          ...(supersededInvoiceId && { replaces_invoice_id: supersededInvoiceId }),
          meta_data,
        },
        token,
      }).unwrap();
      // A newer quote was issued while this one was in flight — drop it.
      if (requestSeqRef.current !== mySeq) return;
      lastInvoiceIdRef.current = response?.invoice_id || null;
      setPaymentBreakdown(response);
    } catch (error) {
      if (requestSeqRef.current !== mySeq) return;
      CommonToast.error(error?.data?.error || "Error preparing payment");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurring, isBundle, membershipPlan, actingCompanyId, token, data, isReportRequest]);

  // 1) Create the payment once a billing address is available, and RE-create it
  // whenever the selected address changes. TIP-646: Stripe's automatic_tax is
  // computed against the customer address on the draft invoice, so a quote
  // raised for a UAE address is simply wrong once the buyer switches to a
  // non-UAE one. Keyed on the chosen address id (and the edit counter, which
  // ticks when the current address is edited in place, e.g. its country).
  const quotedForRef = useRef(null);
  useEffect(() => {
    if (!data || data.length === 0) return;
    const addressKey = `${primary?.billing_add_ID ?? "none"}:${hasAddressDataChanged}`;
    if (quotedForRef.current === addressKey) return;

    const isRequote = quotedForRef.current !== null;
    quotedForRef.current = addressKey;

    if (isRequote) {
      // Drop the stale quote and the Stripe session bound to it, so the
      // summary falls back to ex-VAT rather than showing the old country's
      // total while the new one is being fetched.
      setPaymentBreakdown(null);
      setClientSecret(null);
      setShowStripePayment(false);
    }

    // The first quote has nothing to settle — raise it immediately. A re-quote
    // waits: selecting a new primary address briefly leaves two rows flagged
    // primary while the PATCH lands, and quoting on that intermediate state
    // raises a throwaway invoice for the address being replaced.
    if (!isRequote) {
      createPaymentEvent();
      return undefined;
    }
    const timer = setTimeout(createPaymentEvent, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, primary?.billing_add_ID, hasAddressDataChanged]);

  // 2) Auto-finalize to reveal the Stripe card form (single combined invoice).
  useEffect(() => {
    const finalize = async () => {
      try {
        const finalResponse = await finalizeStripePayment({
          token,
          data: { invoice_id: paymentBreakdown?.invoice_id, recurring },
        });
        const secret = finalResponse?.data?.client_secret;
        if (secret === "PAID") {
          setPaymentSuccessfulModal(true);
        } else if (secret) {
          setClientSecret(secret.toString());
          setPendingSetup(finalResponse?.data?.pending_setup);
          setShowStripePayment(true);
        } else {
          CommonToast.error("Unable to start payment");
        }
      } catch (e) {
        CommonToast.error("Unable to start payment");
      }
    };
    if (paymentBreakdown?.invoice_id && !clientSecret && !showStripePayment) {
      finalize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentBreakdown?.invoice_id]);

  const handleOpenBillingPopup = () => setOpenBillingPopup(!openBillingPopup);
  const handleOpenAddBillingAddressPopup = () => {
    setOpenAddBillingAddressPopup(!openAddBillingAddressPopup);
    setOpenBillingPopup(false);
  };
  const handleOpenEditBillingAddressPopupData = (id) => {
    setBillingId(id);
    setHasAddressDataChanged(!hasAddressDataChanged);
    setOpenEditBillingAddressPopup(!openEditBillingAddressPopup);
    setOpenBillingPopup(false);
  };
  const handleOpenEditBillingAddressPopup = () => {
    setOpenEditBillingAddressPopup(!openEditBillingAddressPopup);
    setOpenBillingPopup(false);
  };
  const onClickDelete = async (d) => {
    try {
      await deleteBillingAddresss({ data: d, token });
      setHasAddressDataChanged(!hasAddressDataChanged);
    } catch (e) {
      CommonToast.error("Error deleting billing address.");
    }
  };
  const { data: getExistBilling, refetch: getExistBillingRefetch } =
    useGetBillingAddressByBillingIdQuery({ id: billingId, token });

  const handlePaymentModalClose = () => {
    setPaymentSuccessfulModal(false);
    navigate("/plans/paymentsuccessful");
  };

  return (
    <>
      <div className="flex-1 w-full bg-white rounded-md p-6 border border-gray-200">
        <div className="flex justify-between items-center pb-1">
          <h3 className="font-semibold text-f18 text-primaryText">Payment details</h3>
          <button type="button" onClick={() => navigate(-1)} className="text-primaryText text-f14">
            Back
          </button>
        </div>
        <p className="text-secondary text-f12 mb-4">{copy}</p>

        {/* Billing address */}
        {userBillingAddress ? (
          <div className="bg-button text-white rounded-md p-4 mb-4 flex justify-between items-start gap-4">
            <div className="text-f12 space-y-1 min-w-0">
              <p className="font-semibold text-f14">{primary?.companyName || userInfo?.name}</p>
              <p>{primary?.mobile}</p>
              <p className="truncate">
                {primary?.address_line_1} {primary?.address_line_2} {primary?.city}{" "}
                {primary?.postal_code} {primary?.country}
              </p>
            </div>
            <button
              onClick={handleOpenBillingPopup}
              className="shrink-0 border border-[rgba(255,255,255,0.3)] bg-[rgba(255,255,255,0.15)] text-f12 text-white py-2 px-3 rounded-md"
            >
              Change address
            </button>
          </div>
        ) : (
          <div className="w-full gap-2 my-2 p-4 border-dashed border-2 flex justify-between items-center rounded-md">
            <p className="text-f14">Add a billing address to continue</p>
            <button
              onClick={handleOpenAddBillingAddressPopup}
              className="bg-primary flex gap-2 items-center justify-center px-4 text-f14 text-white py-2 rounded-md"
            >
              <img style={{ width: "20px" }} src={plusCircle} alt="plus" />
              Add Address
            </button>
          </div>
        )}

        {/* Stripe payment */}
        {userBillingAddress && (
          <div>
            {isCreateLoading || (!clientSecret && !paymentSuccessfulModal) ? (
              <LoadingIcon />
            ) : (
              clientSecret &&
              clientSecret !== "PAID" && (
                <Elements options={options} stripe={stripePromise}>
                  <CheckoutForm
                    name={userInfo?.name}
                    ShippingAddress={shippingAddress}
                    clientSecret={clientSecret}
                    pendingSetup={pendingSetup}
                    title="Web Profile"
                    plan={planDetails}
                    payLabel={<>Pay&nbsp;<Aed value={summary?.total ?? 0} /></>}
                    isReportRequest={isReportRequest}
                  />
                </Elements>
              )
            )}
          </div>
        )}
      </div>

      {openBillingPopup && (
        <Modal modalHandle={handleOpenBillingPopup}>
          <BillingAddress
            refetch={getExistBillingRefetch}
            addressData={data}
            userName={userInfo?.name}
            handleOpenAddBillingAddressPopup={handleOpenAddBillingAddressPopup}
            onClick={(d) => handleOpenEditBillingAddressPopupData(d)}
            onClickDelete={(d) => onClickDelete(d)}
            setHasAddressDataChanged={setHasAddressDataChanged}
            hasAddressDataChanged={hasAddressDataChanged}
            handleOpenBillingPopup={handleOpenBillingPopup}
          />
        </Modal>
      )}
      {openAddBillingAddressPopup && (
        <Modal modalHandle={handleOpenAddBillingAddressPopup} billingAddress={"Back"}>
          <AddBillingAddress
            refetch={getExistBillingRefetch}
            handleOpenAddBillingAddressPopup={handleOpenAddBillingAddressPopup}
            setHasAddressDataChanged={setHasAddressDataChanged}
            hasAddressDataChanged={hasAddressDataChanged}
          />
        </Modal>
      )}
      {openEditBillingAddressPopup && (
        <Modal modalHandle={handleOpenEditBillingAddressPopup} billingAddress={"Back"}>
          <EditBillingAddress
            setOpenBillingPopup={setOpenBillingPopup}
            data={getExistBilling}
            refetch={getExistBillingRefetch}
            setOpenEditBillingAddressPopup={setOpenEditBillingAddressPopup}
            setHasAddressDataChanged={setHasAddressDataChanged}
            hasAddressDataChanged={hasAddressDataChanged}
          />
        </Modal>
      )}
      {paymentSuccessfulModal && (
        <Modal modalHandle={handlePaymentModalClose}>
          <div className="flex flex-col justify-center items-center w-[500px]">
            <LottieAnimation />
            <h2 className="text-f24 font-extrabold">Payment Successful</h2>
            <p className="text-secondary text-center">Your purchase has been completed</p>
            <Button
              onClick={handlePaymentModalClose}
              classes="w-full p-2 bg-primary text-white rounded text-f18 mt-4 flex justify-center items-center"
            >
              Continue
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
